import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Users, Shield, BarChart3, Settings, LogOut, Flag } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminMembers from './AdminMembers';
import AdminStats from './AdminStats';
import AdminSettings from './AdminSettings';
import AdminMonitoring from './AdminMonitoring';
import AdminTestAccounts from './AdminTestAccounts';
import AdminModeration from './AdminModeration';
import { apiService } from '../services/api';

const AdminDashboard = ({ adminInfo, onLogout }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [stats, setStats] = useState(null);
  const [openMember, setOpenMember] = useState(null);
  const { toast } = useToast();
  const openMemberWorkspace = (id, tab) => { setOpenMember({ id, tab }); setActiveTab('users'); };

  const tabs = [
    { id: 'users', label: 'Members', icon: Users },
    { id: 'stats', label: 'Analytics', icon: BarChart3 },
    { id: 'moderation', label: 'Moderation', icon: Flag },
    { id: 'monitoring', label: 'Monitoring', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'test-accounts', label: 'Test Accounts', icon: Shield }
  ];

  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await apiService.admin.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast({
        title: "Error",
        description: "Failed to fetch statistics",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="flex items-center mr-6">
                <Shield className="w-8 h-8 text-purple-600 mr-2" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">FlameLink Admin</h1>
                  <p className="text-sm text-gray-600">Administrator Dashboard</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{adminInfo.username}</p>
                <p className="text-xs text-gray-500">{adminInfo.role}</p>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`admin-tab-${tab.id}`}
                  className={`${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'users' && <AdminMembers openMember={openMember} onConsumed={() => setOpenMember(null)} />}

        {activeTab === 'stats' && (
          <AdminStats stats={stats} />
        )}

        {activeTab === 'moderation' && (
          <AdminModeration onOpenMember={openMemberWorkspace} />
        )}

        {activeTab === 'monitoring' && (
          <AdminMonitoring />
        )}

        {activeTab === 'settings' && (
          <AdminSettings />
        )}

        {activeTab === 'test-accounts' && (
          <AdminTestAccounts />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;